import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, Popover, Progress, Radio, Row, Space, Tooltip, Tree } from "antd";
import { Button } from "Components/General/Button";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import { DataNode } from "antd/lib/tree";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { fileUploadStore } from "./FileUploadStore";
import { observer } from "mobx-react";

export interface TreeZipData extends Omit<API_ArchivedTreeObject, "key" | "title" | "children">, DataNode {
  id: string | number;
  icon: React.ReactNode;
}

interface ZipArchiveContentModalProps extends ModalComponentProps {
  treeData: TreeZipData[];
  extractModes: API_ARCHIVEDOBJECT_EXTRACTMODE[];
  fileId: number;
  onExtract: (mode: API_ARCHIVEDOBJECT_EXTRACTMODE, id: number) => Promise<void>;
}

export const ZipArchiveContentModal: React.FC<ZipArchiveContentModalProps> = observer(
  ({ treeData, fileId, extractModes, onExtract, hideModal }) => {
    const { t } = useTranslation(I18N_NS.PROJECT);
    const [popoverOpen, setOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [type, setType] = useState<API_ARCHIVEDOBJECT_EXTRACTMODE>("NONE");

    const handleExtract = async () => {
      try {
        setLoading(true);
        if (onExtract) {
          if (type !== "NONE") {
            await onExtract(type, fileId);
          }
          setOpen(false);
          hideModal();
          notificationSuccess(t("Success"), "");
        }
      } finally {
        setLoading(false);
      }
    };

    const handleOpenChange = () => {
      setOpen(!popoverOpen);
    };

    const extractTypes: Record<
      API_ARCHIVEDOBJECT_EXTRACTMODE,
      { label: string; value: API_ARCHIVEDOBJECT_EXTRACTMODE; info: string }
    > = {
      NONE: {
        value: "NONE",
        label: "No extraction",
        info: "Upload the unextracted ZIP",
      },
      EXTRACT_FLAT: {
        value: "EXTRACT_FLAT",
        label: "Extract flat",
        info: "Extract all files in one directory",
      },
      EXTRACT_WITH_DIRECTORY: {
        value: "EXTRACT_WITH_DIRECTORY",
        label: "Extract with folders",
        // eslint-disable-next-line max-len
        info: "Extract files and keep directory structure. If you continue the necessary folders will be created immediately.",
      },
      EXTRACT_WITH_EMPTY_DIRECTORY: {
        value: "EXTRACT_WITH_EMPTY_DIRECTORY",
        label: "Keep empty directories",
        // eslint-disable-next-line max-len
        info: "Extract files and keep directory structure incl. empty directories. If you continue the necessary folders will be created immediately.",
      },
    };

    return (
      <div id="archive-content-modal-body">
        <Radio.Group onChange={(e) => setType(e.target.value)} value={type}>
          {extractModes?.map((mode) => {
            const { value, label, info } = extractTypes[mode];
            return (
              <Radio value={value} key={value}>
                <Tooltip title={t(info)}>{t(label)}</Tooltip>
              </Radio>
            );
          })}
        </Radio.Group>
        <Divider />
        <Tree
          className="mb-lg"
          selectable={false}
          height={330}
          style={{ minHeight: 330 }}
          treeData={treeData}
          titleRender={(node: TreeZipData) => (
            <Space>
              <>
                <FontAwesomeIcon icon={["far", node.dir ? "folder" : "file"]} fixedWidth={true} />
                {node.title}
              </>
            </Space>
          )}
        />
        <Row justify="space-between">
          <Button onClick={() => hideModal()}>{t("Cancel")}</Button>
          <Popover
            title={<strong>{t("Your choice")}</strong>}
            getPopupContainer={() => document.getElementById("archive-content-modal-body")}
            content={
              <Row className="flex-column">
                <div className="mb-md" style={{ maxWidth: 500 }}>
                  {t(extractTypes[type].label)} <i>({t(extractTypes[type].info)})</i>
                </div>
                <Row justify="end">
                  <Button className="mr-sm" size="small" type="default" onClick={handleOpenChange}>
                    {t("Cancel")}
                  </Button>
                  <Button type="primary" size="small" onClick={handleExtract} disabled={loading}>
                    {loading ? (
                      <Progress type="circle" percent={fileUploadStore.zipProgress} size={30} />
                    ) : (
                      t("Continue")
                    )}
                  </Button>
                </Row>
              </Row>
            }
            trigger="click"
            open={popoverOpen}
          >
            <Button type="primary" onClick={handleOpenChange}>
              {t("Confirm")}
            </Button>
          </Popover>
        </Row>
      </div>
    );
  }
);
